import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["nameInput", "legalFormSelect", "legalFormHidden", "companyNameHidden", "dropdown", "results"]
  static values = { url: String }

  connect() {
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    this.boundHandleCountryChange = this.handleCountryChange.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('click', this.boundHandleClickOutside)

    // Listen for country changes to update legal form options
    document.addEventListener('country-changed', this.boundHandleCountryChange)
    document.addEventListener('keydown', this.boundHandleKeydown)
    this.legalFormSelectTarget.setAttribute('aria-expanded', 'false')

    // Initialize values from hidden fields
    this.initializeValues()
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)
    document.removeEventListener('country-changed', this.boundHandleCountryChange)
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  initializeValues() {
    // Set initial values from hidden fields
    if (this.hasCompanyNameHiddenTarget) {
      this.nameInputTarget.value = this.companyNameHiddenTarget.value
    }

    if (this.hasLegalFormHiddenTarget && this.legalFormHiddenTarget.value) {
      this.setLegalForm(this.legalFormHiddenTarget.value)
    }

    // Auto-extract legal form from company name if present on page load
    this.autoExtractOnLoad()
  }

  handleCountryChange(event) {
    // Clear legal form when country changes
    this.clearLegalForm()
    this.hideDropdown()
  }

  getCountryId() {
    const countryInput = this.element.closest('form').querySelector('input[name="company[country_id]"]')
    return countryInput ? countryInput.value : null
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  updateCompanyName(event) {
    const value = event.target.value.trim()

    // Auto-extract legal form if name field is typed and legal form is empty
    if (value && !this.legalFormHiddenTarget.value) {
      const extractedData = this.extractLegalFormFromName(value)

      if (extractedData.legalForm) {
        // Set the legal form
        this.setLegalForm(extractedData.legalForm)

        // Clean the company name
        event.target.value = extractedData.cleanName
        this.companyNameHiddenTarget.value = extractedData.cleanName

        // Flash the legal form to show extraction
        this.flashLegalForm()
        return
      }
    }

    // Update hidden field
    this.companyNameHiddenTarget.value = value
  }

  async showLegalFormDropdown(event) {
    const countryId = this.getCountryId()

    if (!countryId) {
      alert('Prašome pirmiau pasirinkti šalį')
      return
    }

    try {
      const response = await this.fetchLegalForms('', countryId)
      this.updateDropdownResults(response)
      this.showDropdown()
    } catch (error) {
      console.error('Error fetching legal forms:', error)
    }
  }

  async searchLegalForms(event) {
    const query = event.target.value.trim()
    const countryId = this.getCountryId()

    if (!countryId) return

    if (query.length > 0) {
      try {
        const response = await this.fetchLegalForms(query, countryId)
        this.updateDropdownResults(response)
        this.showDropdown()
      } catch (error) {
        console.error('Error fetching legal forms:', error)
        this.hideDropdown()
      }
    } else {
      // Show all legal forms for country
      try {
        const response = await this.fetchLegalForms('', countryId)
        this.updateDropdownResults(response)
        this.showDropdown()
      } catch (error) {
        console.error('Error fetching legal forms:', error)
      }
    }
  }

  async fetchLegalForms(query, countryId) {
    const url = new URL(this.urlValue, window.location.origin)
    if (query) url.searchParams.set('query', query)
    url.searchParams.set('country_id', countryId)

    const response = await fetch(url)
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    return response.json()
  }

  updateDropdownResults(legalForms) {
    this.resultsTarget.innerHTML = ''

    // Add "No legal form" option first
    const noLegalFormButton = document.createElement('button')
    noLegalFormButton.type = 'button'
    noLegalFormButton.className = 'w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 rounded-md text-gray-600'
    noLegalFormButton.textContent = 'Nėra teisinės formos'
    noLegalFormButton.dataset.code = ''
    noLegalFormButton.addEventListener('click', (e) => this.selectLegalForm(e))
    this.resultsTarget.appendChild(noLegalFormButton)

    // Add separator if there are legal forms
    if (legalForms.length > 0) {
      const separator = document.createElement('div')
      separator.className = 'border-t border-gray-200 my-1'
      this.resultsTarget.appendChild(separator)
    }

    legalForms.forEach(form => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 rounded-md'
      button.textContent = form.text
      button.dataset.code = form.code
      button.addEventListener('click', (e) => this.selectLegalForm(e))

      this.resultsTarget.appendChild(button)
    })
  }

  selectLegalForm(event) {
    event.preventDefault()
    const code = event.currentTarget.dataset.code
    this.setLegalForm(code)
    this.hideDropdown()
  }

  setLegalForm(code) {
    this.legalFormHiddenTarget.value = code
    const span = this.legalFormSelectTarget.querySelector('span')
    if (span) {
      if (code === '') {
        span.textContent = 'Nėra teisinės formos'
        span.classList.remove('text-gray-900', 'font-medium')
        span.classList.add('text-gray-500')
      } else {
        span.textContent = code
        span.classList.remove('text-gray-500')
        span.classList.add('text-gray-900', 'font-medium')
      }
    }
  }

  clearLegalForm() {
    this.legalFormHiddenTarget.value = ''
    const span = this.legalFormSelectTarget.querySelector('span')
    if (span) {
      span.textContent = 'Pasirinkite'
      span.classList.remove('text-gray-900', 'font-medium')
      span.classList.add('text-gray-500')
    }
  }

  autoExtractOnLoad() {
    // Check if company name contains legal form and extract it
    const companyName = this.nameInputTarget.value.trim()

    if (companyName && !this.legalFormHiddenTarget.value) {
      const extractedData = this.extractLegalFormFromName(companyName)

      if (extractedData.legalForm) {
        // Set the legal form
        this.setLegalForm(extractedData.legalForm)

        // Clean the company name
        this.nameInputTarget.value = extractedData.cleanName
        this.companyNameHiddenTarget.value = extractedData.cleanName
      }
    }
  }

  showDropdown() {
    this.dropdownTarget.classList.remove('hidden')
    this.legalFormSelectTarget.setAttribute('aria-expanded', 'true')
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden')
    this.legalFormSelectTarget.setAttribute('aria-expanded', 'false')
  }

  handleKeydown(event) {
    if (this.dropdownTarget.classList.contains('hidden')) return

    if (event.key === 'Escape') {
      event.preventDefault()
      this.hideDropdown()
      this.legalFormSelectTarget.focus()
      return
    }

    const items = Array.from(this.resultsTarget.querySelectorAll('button'))

    if (items.length === 0) return

    const currentIndex = items.indexOf(document.activeElement)

    switch(event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (currentIndex < 0) {
          items[0].focus()
        } else if (currentIndex < items.length - 1) {
          items[currentIndex + 1].focus()
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (currentIndex <= 0) {
          this.legalFormSelectTarget.focus()
        } else {
          items[currentIndex - 1].focus()
        }
        break
      case 'Enter':
        if (document.activeElement.tagName === 'BUTTON') {
          event.preventDefault()
          document.activeElement.click()
        }
        break
    }
  }

  // European legal forms for detection
  legalForms = [
    'UAB', 'AB', 'MB', 'VšĮ', 'IĮ', 'SP',
    'GmbH', 'AG', 'OHG', 'KG', 'UG',
    'SAS', 'SARL', 'SA', 'SNC', 'EURL',
    'Ltd', 'Plc', 'LLP', 'CIC',
    'ApS', 'A/S', 'K/S', 'I/S', 'AB', 'HB', 'KB', 'AS', 'ASA', 'ANS', 'Oy', 'Oyj', 'Ay', 'Ky',
    'BV', 'NV', 'VOF', 'CV', 'BVBA', 'SPRL', 'SL', 'SLL', 'SCP', 'SRL', 'SpA', 'SNC', 'sp. z o.o.',
    'SE', 'SCE', 'EEIG'
  ]

  extractLegalFormFromName(fullName) {
    const cleaned = fullName.replace(/["""''„"']/, '').replace(/\s+/, ' ').trim()

    for (const form of this.legalForms) {
      const escapedForm = form.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

      // Pattern: "UAB Company Name"
      const prefixMatch = cleaned.match(new RegExp(`^${escapedForm}\\s+(.+)`, 'i'))
      if (prefixMatch) {
        return {
          legalForm: form.toUpperCase(),
          cleanName: prefixMatch[1].trim()
        }
      }

      // Pattern: "Company Name, UAB" or "Company Name ,UAB"
      const suffixCommaMatch = cleaned.match(new RegExp(`(.+),\\s*${escapedForm}$`, 'i'))
      if (suffixCommaMatch) {
        return {
          legalForm: form.toUpperCase(),
          cleanName: suffixCommaMatch[1].trim()
        }
      }

      // Pattern: "Company Name UAB" (no comma)
      const suffixMatch = cleaned.match(new RegExp(`(.+)\\s+${escapedForm}$`, 'i'))
      if (suffixMatch) {
        return {
          legalForm: form.toUpperCase(),
          cleanName: suffixMatch[1].trim()
        }
      }
    }

    return { legalForm: null, cleanName: cleaned }
  }

  flashLegalForm() {
    // Add a subtle highlight to show auto-extraction occurred
    this.legalFormSelectTarget.classList.add('bg-green-50', 'border-green-300')
    setTimeout(() => {
      this.legalFormSelectTarget.classList.remove('bg-green-50', 'border-green-300')
    }, 1500)
  }
}
